import { Module } from "vuex";
import reportsListingService from "@/services/reportsListingService";
import { Report, ReportQueryParams } from "@/interfaces/Report";
import { Platform } from "@/interfaces/Platform";
import extractFilters from "../extractFilters";

interface ReportsState {
  reports: Report[];
  filters: object;
  selectedPlatform: Platform | null;
  loading: boolean;
  loadingReportId: number | null;
  downloadingReport: boolean;
  downloadReportsError: boolean;
  params: ReportQueryParams;
  meta: PaginationMeta;
  error: string | null;
}

const reportsListingModule: Module<ReportsState, any> = {
  namespaced: true,

  state: (): ReportsState => ({
    reports: [],
    filters: {},
    selectedPlatform: null,
    loading: false,
    loadingReportId: null,
    downloadingReport: false,
    downloadReportsError: false,
    params: {
      name: "",
      round: undefined,
      status: undefined,
      sort: "id",
      order: "asc",
      page: 1,
    },
    meta: {
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
      first_page_number: 1,
      last_page_number: 0,
      next_page_number: null,
      prev_page_number: null,
      pages: [],
    },
    error: null,
  }),

  mutations: {
    setReports(state, reports: Report[]) {
      state.reports = reports;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setLoadingReport(state, reportId: number | null) {
      state.loadingReportId = reportId;
    },
    setDownloadingReport(state, downloading: boolean) {
      state.downloadingReport = downloading;
    },
    setDownloadReportsError(state, hasError: boolean) {
      state.downloadReportsError = hasError;
    },
    setParams(state, params: Partial<ReportsState["params"]>) {
      state.params = { ...state.params, ...params };
    },
    setFilters(state, filters) {
      state.filters = filters;
    },
    setPaginationMeta(state, meta: PaginationMeta) {
      state.meta = meta;
    },
    setError(state, error: string | null) {
      state.error = error;
    },
  },

  actions: {
    async fetchReportsListing({ commit, state }) {
      commit("setLoading", true);
      try {
        const response = await reportsListingService.getReportsListing(
          state.params
        );
        const filters = extractFilters(response.filters);

        commit("setReports", response.data);
        commit("setFilters", filters);
        commit("setPaginationMeta", response.meta);
      } catch (error) {
        commit("setError", error || "Failed to fetch reports");
      } finally {
        commit("setLoading", false);
      }
    },

    async downloadReports({ commit, state }, payload) {
      commit("setDownloadingReport", true);
      commit("setDownloadReportsError", false);
      try {
        const response = await reportsListingService.downloadReports(payload);

        if (!response || !response.data) {
          throw new Error("Invalid response from the server.");
        }

        const isMultiple = payload.reportIds.length > 1;

        const selectedReport = state.reports.find(
          (r) => r.id === payload.reportIds[0]
        );
        const isZip = selectedReport?.type?.id === 3;

        const blob = new Blob([response.data], {
          type: isMultiple || isZip ? "application/zip" : "application/pdf",
        });

        const filename = isMultiple
          ? "Reports.zip"
          : payload.fileName || (isZip ? "Report.zip" : "School_Report.pdf");

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading reports:", error);
        commit("setDownloadReportsError", true);
      } finally {
        commit("setDownloadingReport", false);
      }
    },

    updateParams({ commit, dispatch }, newParams) {
      commit("setParams", newParams);
      dispatch("fetchReportsListing");
    },
  },

  getters: {
    allReports: (state) => state.reports,
    isLoading: (state) => state.loading,
    isDownloading: (state) => state.downloadingReport,
    downloadReportsError: (state) => state.downloadReportsError,
    hasError: (state) => !!state.error,
    errorMessage: (state) => state.error,
    loadingReportId: (state) => state.loadingReportId,
    params: (state) => state.params,
  },
};

export default reportsListingModule;
